import { useCallback, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useStore } from 'components/Store';

import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';

import plansApiFunction, { resetOrg } from 'utilities/plansApiFunction';
import PAGINATION_SIZE from 'helpers/globalConstants';
import {
  PLANS_LIST_V2,
  PLANS_LIST_V2_BY_ORG,
  PLAN_SUMMARIES_FETCH_V2,
  PLAN_SUMMARIES_FETCH_V2_BY_ORG
} from 'constants/planServiceV2Flags';

const usePlans = defaultParams => {
  const {
    state: { organization, isGlobalCropSeasonLoading },
    dispatch
  } = useStore();
  const flags = useFlags();
  const orgId = organization?.id;
  const useV2Plans = featureFlagRenderItem(
    flags[PLANS_LIST_V2],
    flags[PLANS_LIST_V2_BY_ORG],
    orgId
  );
  const useV2Summaries = featureFlagRenderItem(
    flags[PLAN_SUMMARIES_FETCH_V2],
    flags[PLAN_SUMMARIES_FETCH_V2_BY_ORG],
    orgId
  );

  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();
  const seasonIdsCommaSeparated = selectedCropSeasonIds.join(',');

  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [plansCount, setPlansCount] = useState(0);
  const [pageNo, setPageNo] = useState(0);
  const [request, setRequest] = useState({
    pageNo,
    size: PAGINATION_SIZE,
    dateRange: [],
    nameQuery: '',
    sortBy: '',
    sortDir: '',
    seasonIdsCommaSeparated,
    ...defaultParams
  });

  const getPlans = useCallback(
    async params => {
      setIsLoading(true);

      const { promise } = await plansApiFunction(
        { ...request, ...params },
        organization,
        { useV2Plans, useV2Summaries }
      );

      await promise
        .then(({ data }) => {
          setPlans(data.results);
          setPlansCount(data.totalElements);
        })
        .catch(catchCancel)
        .catch(err => {
          parseServerError(dispatch)(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [request, dispatch, organization, setIsLoading, useV2Plans, useV2Summaries]
  );

  const reload = useCallback(async () => {
    getPlans();
  }, [getPlans]);

  const reset = () => {
    setRequest(prev => ({
      ...prev,
      pageNo,
      totalPages: 0,
      size: PAGINATION_SIZE,
      seasonIdsCommaSeparated
    }));
  };

  useEffect(() => {
    return () => {
      // destroy fake api records
      resetOrg();
    };
  }, []);

  useEffect(() => {
    if (isGlobalCropSeasonLoading) return;

    if (request.seasonIdsCommaSeparated !== seasonIdsCommaSeparated) {
      setRequest(prev => ({ ...prev, seasonIdsCommaSeparated }));
      return;
    }

    reload();
  }, [reload, request, isGlobalCropSeasonLoading, seasonIdsCommaSeparated]);

  const filterByNameQuery = useCallback(
    newQuery => {
      setRequest(r => ({ ...r, pageNo: 0, nameQuery: newQuery }));
    },
    [setRequest]
  );

  const filterByDateRange = useCallback(
    newDateRange => {
      if (newDateRange.length === 2 || newDateRange.length === 0) {
        setRequest(r => ({ ...r, pageNo: 0, dateRange: newDateRange }));
      }
    },
    [setRequest]
  );

  const goToPage = useCallback(
    newPage => {
      setPageNo(newPage);
      setRequest(r => ({ ...r, pageNo: newPage }));
    },
    [setRequest]
  );

  const sort = useCallback(
    (field, dir) => {
      setRequest(r => ({ ...r, pageNo: 0, sortBy: field, sortDir: dir }));
    },
    [setRequest]
  );

  return {
    getPlans,
    plans,
    plansCount,
    pageNo,
    goToPage,
    isLoading,
    reset,
    reload,
    filterByNameQuery,
    filterByDateRange,
    sort,
    sortBy: request.sortBy,
    sortDir: request.sortDir
  };
};

export default usePlans;
